import axios from "axios";
import Vue from "vue";
import VueAxios from "vue-axios";

const personnelUrl = "/api";

const ApiService = {
    init() {
        Vue.use(VueAxios, axios);
        Vue.axios.defaults.baseURL = "/";
        Vue.axios.defaults.timeout = 30000;

        Vue.axios.interceptors.request.use(
            function(config) {
                return config;
            },
            function(err) {
                return Promise.reject(err);
            }
        );
    },
    setHeader(data) {
        Vue.axios.defaults.headers.common.AzureObjectId =
            data.account.localAccountId;
        Vue.axios.defaults.headers.common[
            "Authorization"
        ] = `Bearer ${data.accessToken}`;
    },
    query(resource, params) {
        return Vue.axios.get(resource, params).catch(error => {
            throw new Error(`[RWV] ApiService ${error}`);
        });
    },

    get(baseUrl, resource, slug = "") {
        return Vue.axios.get(`${baseUrl}/${resource}/${slug}`).catch(error => {
            throw new Error(`[RWV] ApiService ${error}`);
        });
    },

    post(baseUrl, resource, slug, params) {
        const url = `${baseUrl}/${resource}/${slug}`;
        return Vue.axios.post(url, params);
    },

    put(baseUrl, resource, slug, params) {
        return Vue.axios.put(`${baseUrl}/${resource}/${slug}`, params);
    },

    delete(baseUrl, resource) {
        return Vue.axios.delete(`${baseUrl}/${resource}`).catch(error => {
            throw new Error(`[RWV] ApiService ${error}`);
        });
    }
};

export default ApiService;

export const PersonnelService = {
    getAllAssets(slug) {
        return ApiService.get(personnelUrl, "personAssets", slug);
    },

    getAllPersonAssets(slug) {
        return ApiService.get(personnelUrl, "personAssets", slug);
    },

    getAssetTypes() {
        return ApiService.get(personnelUrl, "assetTypes", "");
    },

    getAssetStatus() {
        return ApiService.get(personnelUrl, "assetStatus", "");
    },

    getPersonnelCountries() {
        return ApiService.get(personnelUrl, "personnelCountries", "");
    },

    saveNewPerson(person) {
        return ApiService.post(personnelUrl, "person", "", person);
    },
    getAllDepartments(slug) {
        return ApiService.get(personnelUrl, "departments", slug);
    },

    query(type, params) {
        return ApiService.query(
            "personnel" + (type === "feed" ? "/feed" : ""),
            {
                params: params
            }
        );
    },
    getAllEmploymentTypes(slug) {
        return ApiService.get(personnelUrl, "employmentTypes", slug);
    },
    getAllEcsCompanies(slug) {
        return ApiService.get(personnelUrl, "ecsCompanies", slug);
    },
    getAllCourseTypes(slug) {
        return ApiService.get(personnelUrl, "courseTypes", slug);
    },
    getAllAppPermissions(slug) {
        return ApiService.get(personnelUrl, "appPermissions", slug);
    },
    getAllRoleEcsApps(slug) {
        return ApiService.get(personnelUrl, "roleEcsApp", slug);
    },
    getAllEcsApps(slug) {
        return ApiService.get(personnelUrl, "ecsAppz", slug);
    },
    getAllRoles(slug) {
        return ApiService.get(personnelUrl, "role", slug);
    },
    getAllPersonnel(slug) {
        return ApiService.get(personnelUrl, "person", slug);
    },

    getEcsCompany(ecsCompanyId) {
        return ApiService.get(
            personnelUrl,
            `ecsCompanies/${ecsCompanyId}`,
            ""
        );
    },
    
    getCourseType(id) {
        return ApiService.get(
            personnelUrl,
            `courseTypes/${id}`,
            ""
        );
    },

    getJob(jobId) {
        return ApiService.get(personnelUrl, `job/${jobId}`, "");
    },
    getRole(roleId) {
        return ApiService.get(personnelUrl, `role/${roleId}`, "");
    },
    getRoleEcsApps(roleId) {
        return ApiService.get(personnelUrl, `roleEcsApp/${roleId}`, "");
    },

    getDepartment(departmentId) {
        return ApiService.get(
            personnelUrl,
            `departments/${departmentId}`,
            ""
        );
    },

    getTeam(teamId) {
        return ApiService.get(personnelUrl, `team/${teamId}`, "");
    },

    getPerson(personId) {
        return ApiService.get(personnelUrl, `person/${personId}`, "");
    },
    getPersonAppPermissions(personId) {
        return ApiService.get(
            personnelUrl,
            `permission/${personId}`,
            ""
        );
    },
    getPersonAddress(personId) {
        return ApiService.get(
            personnelUrl,
            `personAddresses/${personId}`,
            ""
        );
    },
    getPersonTeam(personId) {
        return ApiService.get(
            personnelUrl,
            `personTeams/${personId}`,
            ""
        );
    },
    getPersonPhoneNumbers(personId) {
        return ApiService.get(
            personnelUrl,
            `personPhoneNumbers/${personId}`,
            ""
        );
    },
    getPersonEmergencyContacts(personId) {
        return ApiService.get(
            personnelUrl,
            `personEmergencyContacts/${personId}`,
            ""
        );
    },
    getPersonnelEmployments() {
        return ApiService.get(personnelUrl, `personEmployments/`, "");
    },
    getPersonnelTeams() {
        return ApiService.get(personnelUrl, `personTeams/`, "");
    },
    getPersonEmployments(personId) {
        return ApiService.get(
            personnelUrl,
            `personEmployments/${personId}`,
            ""
        );
    },
    getPersonHolidayEntitlements(personId) {
        return ApiService.get(
            personnelUrl,
            `personHolidayEntitlements/${personId}`,
            ""
        );
    },
    getPersonAvailabilitys(personId) {
        return ApiService.get(
            personnelUrl,
            `personAvailabilities/${personId}`,
            ""
        );
    },
    getPersonVehicles(personId) {
        return ApiService.get(
            personnelUrl,
            `personVehicles/${personId}`,
            ""
        );
    },
    getPersonCourses(personId) {
        return ApiService.get(
            personnelUrl,
            `personCourses/${personId}`,
            ""
        );
    },
    getPersonAssets() {
        return ApiService.get(personnelUrl, `personAssets`, "");
    },
    getPersonAssetsById(personId) {
        return ApiService.get(
            personnelUrl,
            `personAssets/${personId}`,
            ""
        );
    },

    create(params) {
        return ApiService.post(personnelUrl, "personnel", {
            article: params
        });
    },
    saveNewTeam(team) {
        return ApiService.post(personnelUrl, "team", "", team);
    },
    saveNewAsset(asset) {
        return ApiService.post(personnelUrl, "personAssets", "", asset);
    },
    savePersonAsset(personAsset) {
        return ApiService.post(
            personnelUrl,
            "personAssets",
            "",
            personAsset
        );
    },
    savePersonCourse(personCourse) {
        return ApiService.post(
            personnelUrl,
            "personCourses",
            "",
            personCourse
        );
    },
    saveNewPersonVehicle(personVehicle) {
        return ApiService.post(
            personnelUrl,
            "personVehicles",
            "",
            personVehicle
        );
    },
    updatePersonHolidayEntitlement(personHolidayEntitlement) {
        const personHolidayEntitlementId =
            personHolidayEntitlement.personHolidayEntitlementId;
        return ApiService.put(
            personnelUrl,
            `personHolidayEntitlements/${personHolidayEntitlementId}`,
            "",
            personHolidayEntitlement
        );
    },
    updatePersonAvailability(personAvailability) {
        const personAvailabilityId =
            personAvailability.personAvailabilityId;
        return ApiService.put(
            personnelUrl,
            `personAvailabilities/${personAvailabilityId}`,
            "",
            personAvailability
        );
    },
    updatePersonAsset(personAsset) {
        const personAssetId = personAsset.assetId;
        return ApiService.put(
            personnelUrl,
            `personAssets/${personAssetId}`,
            "",
            personAsset
        );
    },
    updatePersonCourse(personCourse) {
        const personCourseId = personCourse.id;
        return ApiService.put(
            personnelUrl,
            `personCourses/${personCourseId}`,
            "",
            personCourse
        );
    },
    updatePersonVehicle(personVehicle) {
        const personVehicleId = personVehicle.vehicleId;
        return ApiService.put(
            personnelUrl,
            `personVehicles/${personVehicleId}`,
            "",
            personVehicle
        );
    },
    saveTeamPerson(personTeam) {
        return ApiService.post(
            personnelUrl,
            `personTeams/`,
            "",
            personTeam
        );
    },
    savePersonHolidayEntitlement(personHolidayEntitlement) {
        personHolidayEntitlement.carriedOver =
            personHolidayEntitlement.carriedOver * 480;
        personHolidayEntitlement.yearlyEntitlement =
            personHolidayEntitlement.yearlyEntitlement * 480;
        personHolidayEntitlement.specialAllowance =
            personHolidayEntitlement.specialAllowance * 480;

        return ApiService.post(
            personnelUrl,
            "personHolidayEntitlements",
            "",
            personHolidayEntitlement
        );
    },
    savePersonAvailability(personAvailability) {
        return ApiService.post(
            personnelUrl,
            "personAvailabilities",
            "",
            personAvailability
        );
    },
    savePersonVehicle(personVehicle) {
        return ApiService.post(
            personnelUrl,
            "personVehicles",
            "",
            personVehicle
        );
    },
    savePersonTeam(personTeam) {
        return ApiService.post(
            personnelUrl,
            "personTeams",
            "",
            personTeam
        );
    },
    savePersonEmployment(personEmployment) {
        return ApiService.post(
            personnelUrl,
            "personEmployments",
            "",
            personEmployment
        );
    },
    savePersonEmergencyContact(personEmergencyContact) {
        return ApiService.post(
            personnelUrl,
            "personEmergencyContacts",
            "",
            personEmergencyContact
        );
    },
    savePersonPhoneNumber(personPhoneNumber) {
        return ApiService.post(
            personnelUrl,
            "personPhoneNumbers",
            "",
            personPhoneNumber
        );
    },
    deletePersonEmployment(personEmploymentId) {
        return ApiService.delete(
            personnelUrl,
            `personEmployments/${personEmploymentId}`
        );
    },
    deletePersonVehicle(personVehicleId) {
        return ApiService.delete(
            personnelUrl,
            `personVehicles/${personVehicleId}`
        );
    },
    deletePersonCourse(personCourseId) {
        return ApiService.delete(
            personnelUrl,
            `personCourses/${personCourseId}`
        );
    },
    deletePersonHolidayEntitlement(personHolidayEntitlementId) {
        return ApiService.delete(
            personnelUrl,
            `personHolidayEntitlements/${personHolidayEntitlementId}`
        );
    },
    deletePersonAvailability(personAvailabilityId) {
        return ApiService.delete(
            personnelUrl,
            `personAvailabilities/${personAvailabilityId}`
        );
    },
    deletePersonTeam(personTeamId) {
        return ApiService.delete(
            personnelUrl,
            `personTeams/${personTeamId}`
        );
    },
    deletePersonEmergencyContact(personEmergencyContactId) {
        return ApiService.delete(
            personnelUrl,
            `personEmergencyContacts/${personEmergencyContactId}`
        );
    },
    deletePersonPhoneNumber(personPhoneNumberId) {
        return ApiService.delete(
            personnelUrl,
            `personPhoneNumbers/${personPhoneNumberId}`
        );
    },
    deletePersonAsset(personAssetId) {
        return ApiService.delete(
            personnelUrl,
            `personAssets/${personAssetId}`
        );
    },
    updatePersonPersonSummarys(person) {
        const personId = person.personId;

        let newPersonPersonSummarys = {};
        newPersonPersonSummarys = person;

        return ApiService.put(
            personnelUrl,
            "person",
            personId,
            newPersonPersonSummarys
        );
    },

    updatePersonEmergencyContact(personEmergencyContact) {
        let personEmergencyContactId =
            personEmergencyContact.personEmergencyContactId;

        let newPersonPersonSummarys = {};
        newPersonPersonSummarys = personEmergencyContact;

        // Temp fix because you cant send both object and id for update
        delete newPersonPersonSummarys["job"];
        delete newPersonPersonSummarys["ecsCompany"];
        delete newPersonPersonSummarys["team"];

        return ApiService.put(
            personnelUrl,
            "personEmergencyContacts",
            personEmergencyContactId,
            newPersonPersonSummarys
        );
    },

    updatePersonEmployment(person) {
        let personEmploymentId = person.personEmploymentId;
        let newPersonPersonSummarys = {};
        newPersonPersonSummarys = person;

        // Temp fix because you cant send both object and id for update
        delete newPersonPersonSummarys["job"];
        delete newPersonPersonSummarys["ecsCompany"];
        delete newPersonPersonSummarys["team"];

        return ApiService.put(
            personnelUrl,
            "personEmployments",
            personEmploymentId,
            newPersonPersonSummarys
        );
    },

    updatePersonAddress(personAddress) {
        let personAddressId = personAddress.personAddressId;

        return ApiService.put(
            personnelUrl,
            "personAddresses",
            personAddressId,
            personAddress
        );
    },

    updatePersonTeam(personTeam) {
        let personTeamId = personTeam.personTeamId;

        return ApiService.put(
            personnelUrl,
            "personTeams",
            personTeamId,
            personTeam
        );
    },

    updatePersonPhoneNumber(personPhoneNumber) {
        let personPhoneNumberId = personPhoneNumber.personPhoneNumberId;
        return ApiService.put(
            personnelUrl,
            "personPhoneNumbers",
            personPhoneNumberId,
            personPhoneNumber
        );
    },

    updateEcsCompanyDetails(ecsCompany) {
        let ecsCompanyId = ecsCompany.ecsCompany.ecsCompanyId;
        let newecsCompanyDetails = {};
        newecsCompanyDetails = ecsCompany.ecsCompany;
        return ApiService.put(
            personnelUrl,
            "ecsCompanies",
            ecsCompanyId,
            newecsCompanyDetails
        );
    },

    updateCourseTypeDetails(courseType) {
        let courseTypeId = courseType.courseType.id;
        let newCourseTypeDetails = {};
        newCourseTypeDetails = courseType.courseType;
        return ApiService.put(
            personnelUrl,
            "courseTypes",
            courseTypeId,
            newCourseTypeDetails
        );
    },

    updateJobDetails(job) {
        let jobId = job.job.jobId;
        let newJobDetails = {};
        newJobDetails = job.job;
        return ApiService.put(
            personnelUrl,
            "jobs",
            jobId,
            newJobDetails
        );
    },

    updateTeamDetails(team) {
        let teamId = team.team.teamId;
        let newTeamDetails = {};
        newTeamDetails = team.team;
        return ApiService.put(
            personnelUrl,
            "teams",
            teamId,
            newTeamDetails
        );
    },

    getAllJobs(slug) {
        return ApiService.get(personnelUrl, "jobs", slug);
    },
    updateJob(job) {
        let jobId = job.jobId;
        return ApiService.put(personnelUrl, "jobs", jobId, job);
    },
    saveNewJob(job) {
        return ApiService.post(personnelUrl, "jobs", "", job);
    },

    getAllTeams(slug) {
        return ApiService.get(personnelUrl, "teams", slug);
    },

    getCountryList() {
        return ApiService.get(personnelUrl, "countries", "");
    },

    getCurrencyList() {
        return ApiService.get(personnelUrl, "currencies", "");
    },

    updateTeam(team) {
        let teamId = team.teamId;
        return ApiService.put(personnelUrl, "teams", teamId, team);
    },

    saveTeam(team) {
        return ApiService.post(personnelUrl, "teams", "", team);
    },

    updateEcsCompany(ecsCompany) {
        let ecsCompanyId = ecsCompany.ecsCompanyId;

        return ApiService.put(
            personnelUrl,
            "ecsCompanies",
            ecsCompanyId,
            ecsCompany
        );
    },

    updateCourseType(courseType) {
        let courseTypeId = courseType.id;
        return ApiService.put(
            personnelUrl,
            "courseTypes",
            courseTypeId,
            courseType
        );
    },

    saveEcsCompany(ecsCompany) {
        return ApiService.post(
            personnelUrl,
            "ecsCompanies",
            "",
            ecsCompany
        );
    },

    saveCourseType(courseType) {
        return ApiService.post(
            personnelUrl,
            "courseTypes",
            "",
            courseType
        );
    },

    sendDetailsConfirmation(personnelId) {
        return ApiService.post(
            personnelUrl,
            `person/confirmdetails`,
            personnelId,
            null
        );
    },

    destroy(slug) {
        return ApiService.delete(personnelUrl, `personnel/${slug}`);
    }
};
